import { PaperSize } from "@client-shared/types/pdfPrint";
import { COUNTRIES_WITH_LOCALES, REGIONS_WITH_LOCALES } from "@shared/lib/constants";
import type { VendorCountryCode } from "@shared/schemas";
import type { VendorRegion } from "@shared/types";
import type { GrindType } from "@shared/types/connection";
import type { CalendarDateFormat, CalendarLocale, CurrencyIsoCode, Language, Locale, LocaleObject } from "@shared/types/localization";
import type { ServiceId } from "@shared/types/service";
import { PaginationState } from "@tanstack/react-table";
import { Accept } from "react-dropzone";

// PDF paper sizes available for shipping documents
export const PAPER_SIZES: PaperSize[] = ["3x4", "4x6", "A6", "A4", "Letter"];

// common hidden export columns (kept for backward compatibility)
export const HIDDEN_EXPORT_COLUMNS = ["vendorKey", "isDisabled", "isLoading", "hasError", "tone", "errors", "errorMessage"];

// Visible columns for order and batch order CSV exports (whitelist approach)
export const ORDER_VISIBLE_EXPORT_COLUMNS = [
  "batchId", // Batch
  "purchaseOrderId", // Order
  "orderStatus", // Status
  "shippingAddress.zip", // Zip
  "shippingAddress.country", // Country
  "shippingAddress.province", // Province
  "shippingAddress.phone", // Phone
  "shippingAddress.address2", // Address 2
  "shippingAddress.city", // City
  "shippingAddress.address1", // Address 1
  "shippingAddress.name", // Name
  "shippingAddress.company", // Company
  "orderTotal", // Order total
  "shippingAmount", // Shipping amount
  "createdAt", // Created
  "orderWeightInGrams", // Weight
  "customerAccountName", // Marketplace
  "lineItems.quantity", // Quantity
  "lineItems.carrier", // Carrier
  "lineItems.trackingNumber", // Tracking
  "lineItems.trackingUrl", // Tracking URL
  "lineItems.lineItemWeightInGrams", // Line item weight
  "lineItems.productTitle", // Product
  "lineItems.grindType", // Grind
  "lineItems.labelCreatedDate", // Label created
  "lineItems.carrierScanDate", // Carrier scan date
  "lineItems.sourceProductSku", // SKU
  "lineItems.exportLabelUrl", // Shipping label
];
export const SHIPPING_LABEL_POLLING_INTERVAL = 15000;

// Mirrors D365's own carrier names (see shared/src/services/carrierDetails/types.ts) and must be
// kept in sync with CarriersPrintLabel by hand - there is no shared source of truth between the
// frontend and shared/ packages for this list.
//
// The mixed spacing below (e.g. "RoyalMail" vs "Australia Post") is D365's inconsistency, not
// ours - do not "tidy" it. Each value must match the carrier name registered in D365 exactly,
// since it round-trips through CarrierProfile.d365CarrierName/.carrier in DynamoDB. Commit
// 99415620d normalized "Australia Post" -> "AustraliaPost" and had to be reverted wholesale in
// 2b6de3872 because of D365 issues.
export const SHIPPING_CARRIERS = [
  "DPD",
  "DHL",
  "FedEx",
  "UPS",
  "USPS",
  "RoyalMail",
  "PostNL",
  "Australia Post",
  "Shippit",
  "StarTrack",
  "DHL PAKET",
] as const;

export type ShippingCarrier = (typeof SHIPPING_CARRIERS)[number];

export const isShippingCarrier = (value: string): value is ShippingCarrier => (SHIPPING_CARRIERS as readonly string[]).includes(value);

export const INTERNAL_CUSTOMER_ACCOUNT_DISPLAY_NAME = "Beanz";
export const DEFAULT_MARKETPLACE_NAME = "Beanz.com";
// DATATABLE
export const DEFAULT_DATATABLE_PAGINATION: PaginationState = {
  pageIndex: 0,
  pageSize: 50,
};
export const MAX_PURCHASE_ORDER_CONNECTIONS_LIMIT = 2000;
export const DEFAULT_QUERY_LIMIT = 2000;
export const DEFAULT_QUERY_MAX = 15000;

// Page sizes for fetchAllPurchaseOrders progressive loading. The first page is
// kept small for fast initial render; subsequent pages are sized to keep Lambda
// response payloads safely under the 6 MB API Gateway ceiling.
export const ORDERS_FIRST_PAGE_LIMIT = 250;
export const ORDERS_PAGE_LIMIT = 1000;

// API batch size for bulk shipping document requests
export const API_BATCH_SIZE = 200;
export const API_BATCH_SIZE_LARGE = 100; // For requests with >500 orders (more likely to hit 502s)
export const API_BATCH_SIZE_THRESHOLD = 500; // Use smaller batches when order count exceeds this
export const API_BATCH_CONCURRENCY = 15;

// Streaming PDF processing constants

/**
 * Number of PDFs to fetch in parallel.
 * Higher values may improve throughput but can cause memory or network saturation,
 * while lower values may slow down processing. Increased from 10 to 20 to improve
 * performance for large datasets (1000+ orders).
 */
export const FETCH_CONCURRENCY = 20;

/**
 * Timeout (in milliseconds) for each PDF fetch request.
 * If set too low, legitimate requests may fail; too high may delay error handling.
 * 30 seconds was selected as a reasonable upper bound based on observed network latency.
 */
export const FETCH_TIMEOUT_MS = 30000;

/**
 * Maximum number of retry attempts for failed PDF fetches.
 * More retries can improve resilience to transient errors but may increase total processing time.
 * Value of 2 was chosen to balance reliability and speed, based on error rates in production.
 */
export const MAX_PDF_FETCH_RETRIES = 2;

/**
 * Minimum number of orders required to enable streaming PDF processing.
 * Lowering this threshold may enable streaming for small batches (with little benefit),
 * while raising it may delay streaming for large batches. 50 was chosen based on
 * performance testing to optimize for typical order volumes.
 */
export const USE_STREAMING_THRESHOLD = 50;

/**
 * Number of parallel PDF fitting operations.
 * Higher values may speed up processing but increase CPU/memory usage.
 * 5 was selected as a safe default after testing on typical hardware.
 */
export const FIT_CONCURRENCY = 5;

/**
 * Polling interval (in milliseconds) when waiting for the generator to yield more URLs.
 * Lower values reduce latency but increase CPU usage; higher values may slow down processing.
 * 10ms was chosen as a compromise between responsiveness and resource usage.
 */
export const GENERATOR_POLL_INTERVAL_MS = 10;

/**
 * Multiplier for queue size calculation (queue size = concurrency * multiplier).
 * Higher values allow more items to be queued, which can improve throughput but increase memory usage.
 * 2 was chosen based on empirical observation to avoid bottlenecks without excessive memory growth.
 */
export const QUEUE_SIZE_MULTIPLIER = 2;

// Combined PDF chunk processing constants
export const COMBINED_PDF_CHUNK_SIZE = 100; // Default chunk size for processing combined PDFs to limit memory usage
export const COMBINED_PDF_CHUNK_SIZE_LARGE = 50; // Chunk size for datasets >= 2000
export const COMBINED_PDF_CHUNK_THRESHOLD = 2000; // Single threshold for using large chunk size
/**
 * Number of parallel PDF fitting operations for chunked slip generation.
 *
 * Increased from 10 to 20 to improve processing speed for large datasets (1000+ orders).
 *
 * ⚠️ Resource impact: Since chunks are processed sequentially, this results in up to
 * FIT_CONCURRENCY_CHUNKED (20) concurrent PDF fitting operations during slip generation
 * phase (one chunk at a time). During label processing phase, up to FETCH_CONCURRENCY (20)
 * concurrent PDF fetch/fit operations can occur. Monitor for:
 * - Browser freezing on lower-end devices
 * - Memory exhaustion with very large datasets
 * - CPU throttling
 *
 * If issues occur, reduce to 10-15 or implement adaptive concurrency based on device capabilities.
 */
export const FIT_CONCURRENCY_CHUNKED = 20;
/**
 * Number of chunks to process in parallel for combined PDF generation.
 *
 * NOTE: Currently not used - chunks are processed sequentially to maintain page order.
 * This constant is kept for potential future use if buffering approach is implemented.
 */
export const CHUNK_PROCESSING_CONCURRENCY = 3;

// Dataset size thresholds
export const LARGE_DATASET_THRESHOLD = 250; // Single threshold for large datasets

// PDF cache settings
export const PDF_CACHE_MAX_AGE_LARGE_MS = 5 * 60 * 1000; // 5 minutes for large datasets
export const PDF_CACHE_MAX_AGE_SMALL_MS = 60 * 60 * 1000; // 1 hour for small datasets
export const PDF_CACHE_CLEANUP_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
export const PDF_CACHE_MAX_SIZE_LARGE = 3; // Max cache size for large datasets
export const PDF_CACHE_MAX_SIZE_SMALL = 20; // Max cache size for small datasets
export const CACHE_USAGE_THRESHOLD = 10; // Use cache when orders <= this threshold

// Batch size constants (for getBatchSize function)
export const BATCH_SIZE_THRESHOLD_LARGE = 2000; // Single threshold - max dataset size
export const BATCH_SIZE_LARGE = 5; // For datasets > 2000
export const BATCH_SIZE_LARGE_LABEL = 10; // For labels when dataset > 2000
export const BATCH_SIZE_LARGE_SLIP = 8; // For slips when dataset > 2000
export const BATCH_SIZE_DEFAULT = 15; // For datasets <= 2000

// PDF processing constants
export const PROCESS_URLS_MAX_ITERATIONS = 100000; // Safety limit for URL processing loops
export const PROCESS_URLS_MAX_DURATION_MS = 5 * 60 * 1000; // 5 minutes - maximum duration for URL processing
export const TOAST_AUTO_CLOSE_MS = 10000; // 10 seconds
export const PDF_MARGIN_DEFAULT = 0; // Default margin for PDF fitting operations
export const DEFAULT_PAPER_SIZE: PaperSize = "4x6"; // Fallback paper size

// CURRENCIES AND REGIONS
export type CurrencyObject = { symbol: string; name: string; iso: CurrencyIsoCode; position?: "prefix" | "suffix" };

export const REGIONS_WITH_CURRENCY: { [key in VendorRegion]: CurrencyObject } = {
  BAU: { symbol: "$", name: "Australian dollars", iso: "AUD", position: "prefix" },
  BUK: { symbol: "£", name: "pound sterling", iso: "GBP", position: "prefix" },
  BUS: { symbol: "$", name: "dollars", iso: "USD", position: "prefix" },
  BDE: { symbol: "€", name: "euros", iso: "EUR", position: "suffix" },
};

export const REGIONS_WITH_LOCALE: { [key in VendorRegion]: Locale } = {
  BAU: "en_AU",
  BUK: "en_GB",
  BUS: "en_US",
  BDE: "de_DE",
};

export const COUNTRY_CODE_TO_VENDOR_REGION: Record<string, VendorRegion> = {
  US: "BUS",
  GB: "BUK",
  AU: "BAU",
  DE: "BDE",
  NL: "BDE",
} as const;

/**
 * Maps a vendor's Salesforce BillingAddress countryCode directly to a Locale.
 * Used as a stop-gap so the config provider can derive a
 * "content locale" for grind/bag-size detection when a roaster sits in a
 * multi-locale region — e.g. an NL roaster on BDE — and the region's default
 * locale (de_DE) is the wrong language for the roaster's product data.
 *
 * Note: this is intentionally a separate table from COUNTRY_CODE_TO_VENDOR_REGION
 * because the goal is the locale, not the region — the roaster stays on BDE,
 * we just want the Dutch label set for detection.
 */
export const COUNTRY_CODE_TO_LOCALE: Record<string, Locale> = {
  US: "en_US",
  GB: "en_GB",
  AU: "en_AU",
  DE: "de_DE",
  NL: "nl_NL",
};

export { COUNTRIES_WITH_LOCALES, REGIONS_WITH_LOCALES };

export type LocaleSource = "region" | "country";

export const normalizeVendorCountryCode = (countryCode: string | null | undefined): VendorCountryCode | undefined => {
  const normalized = countryCode?.trim().toUpperCase();
  if (!normalized || !Object.hasOwn(COUNTRIES_WITH_LOCALES, normalized)) {
    return undefined;
  }
  return normalized as VendorCountryCode;
};

export const getLocalesForVendorCountry = (countryCode: string | null | undefined): LocaleObject[] | undefined => {
  const normalized = normalizeVendorCountryCode(countryCode);
  return normalized ? COUNTRIES_WITH_LOCALES[normalized] : undefined;
};

export const getDefaultLocaleForCountry = (countryCode: string | null | undefined): Locale | undefined => {
  return getLocalesForVendorCountry(countryCode)?.find((localeObj) => localeObj.isDefault)?.locale;
};

export const getDefaultLocaleForRegion = (region: string): Locale | undefined => {
  const locales = REGIONS_WITH_LOCALES[region as VendorRegion];
  return locales?.find((localeObj) => localeObj.isDefault)?.locale || undefined;
};

export const userProfileLanguageOptions = [
  { label: "English (Germany)", value: "en_DE" },
  { label: "English (Netherlands)", value: "en_NL" },
  { label: "English", value: "en_US" },
  { label: "German", value: "de" },
  { label: "Dutch", value: "nl" },
  { label: "Dutch (Netherlands)", value: "nl_NL" },
  { label: "German (Germany)", value: "de_DE" },
  { label: "English (United Kingdom)", value: "en_GB" },
  { label: "English (Australia)", value: "en_AU" },
];

export interface LanguageObject {
  name: Language;
  default: Locale;
  locales: Locale[];
}

export type CalendarLocalisationObject = {
  format: CalendarDateFormat;
  calendarLocale: CalendarLocale;
};

export const LOCALES_WITH_CALENDAR_DATE_FORMAT: { [key in Locale]: CalendarLocalisationObject } = {
  en_GB: { format: "dd/MM/yyyy", calendarLocale: "en-GB" },
  en_AU: { format: "dd/MM/yyyy", calendarLocale: "en-AU" },
  en_US: { format: "MM/dd/yyyy", calendarLocale: "en-US" },
  de_DE: { format: "dd/MM/yyyy", calendarLocale: "de" },
  en_DE: { format: "dd/MM/yyyy", calendarLocale: "de" },
  nl_NL: { format: "dd/MM/yyyy", calendarLocale: "nl" },
  en_NL: { format: "dd/MM/yyyy", calendarLocale: "nl" },
  en: { format: "MM/dd/yyyy", calendarLocale: "en-US" },
  de: { format: "dd/MM/yyyy", calendarLocale: "de" },
  nl: { format: "dd/MM/yyyy", calendarLocale: "nl" },
  es: { format: "dd/MM/yyyy", calendarLocale: "es" },
};

export const DEFAULT_USER_LOCALE: Locale = "en_US";

// TRACKING LINKS
export type TrackingCarrier = "DEFAULT" | "USPS" | "USPS STAMPS" | "FedEx" | "UPS" | "DHL express";

export const TRACKING_LINKS: { [key in TrackingCarrier]: string } = {
  DEFAULT: "https://www.google.com/search?q=",
  USPS: "https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=",
  "USPS STAMPS": "https://www.stamps.com/shipstatus/submit/?confirmation=",
  FedEx: "https://www.fedex.com/Tracking?tracknumbers=",
  UPS: "https://wwwapps.ups.com/WebTracking/track?track=yes&trackNums=",
  "DHL express": "https://track.dhl-usa.com/TrackByNbr.asp?ShipmentNumber=",
};

export const ROUTE_PATHS = {
  BACK: -1,
  HOME: "/",
  CALLBACK: "/oauth/callback",
  LOGIN: "/oauth/login",
  LOGOUT: "/oauth/logout",
  OAUTH: "/oauth/*",
  ADMIN_ROASTERS: "/admin/roasters",
  ONBOARDING: "/onboarding",
  ONBOARDING_MAP_PRODUCTS: "/onboarding/map-products",
  ONBOARDING_SUCCESS: "/onboarding/success",
  SETTINGS: "/settings",
  PRODUCTS: "/products",
  ORDERS: "/orders",
  ORDERS_WITH_PURCHASE_ORDER: "/orders/:purchaseOrderId",
  MARKETPLACE: "/marketplace",
  MARKETPLACE_ORDERS: "/marketplace/:customerAccountsId/orders",
  MARKETPLACE_ORDERS_WITH_PURCHASE_ORDER: "/marketplace/:customerAccountsId/orders/:purchaseOrderId",
  BATCHES: "/batches",
  BATCHES_WITH_BATCH: "/batches/:batchId",
  BATCHES_WITH_BATCH_SUMMARY: "/batches/:batchId/summary",
  BATCHES_WITH_BATCH_AND_ORDER: "/batches/:batchId/:purchaseOrderId",
  PAYOUTS: "/finance/invoices",
};

export const SEARCH_PARAMS = {
  INVOICESTATUS: "invoiceStatus",
};

export const AUTH_PATHS = [ROUTE_PATHS.LOGIN, ROUTE_PATHS.LOGOUT, ROUTE_PATHS.CALLBACK];

export const REQUIRED_GRIND: GrindType = "Whole bean";

// image upload constants
export const MAX_UPLOAD_SIZE_MB = 3;
export const ACCEPTED_FILE_TYPES: Accept = {
  "image/png": [".png"],
  "image/jpeg": [".jpeg", ".jpg"],
};
export const MIN_IMAGE_WIDTH = 600;
export const MIN_IMAGE_HEIGHT = 600;
export const MAX_URL_IMAGE_LONGEST_EDGE = 600;
export const THUMBNAIL_SIZE = 200;
export const SHOPIFY_URL_MODIFIER = `&height=${MAX_URL_IMAGE_LONGEST_EDGE}&width=${MAX_URL_IMAGE_LONGEST_EDGE}`;
export const SHOPIFY_THUMBNAIL_URL_MODIFIER = `&height=${THUMBNAIL_SIZE}&width=${THUMBNAIL_SIZE}`;
export const SERVICE_IMAGE_MODIFIERS: { [key in ImageService]: ImageServiceHelpers } = {
  shopify: {
    id: "shopify",
    modifier: SHOPIFY_URL_MODIFIER,
    thumbModifier: SHOPIFY_THUMBNAIL_URL_MODIFIER,
  },
  woocommerce: {
    id: "woocommerce",
  },
};

export type ImageService = Exclude<ServiceId, "pim" | "d365" | "shipstation" | "pbb" | "fg" | "fg-pbb">;

export type ImageServiceHelpers = {
  id: ImageService;
  modifier?: string;
  thumbModifier?: string;
};

// navigation constants
export type RouteName = "home" | "batches" | "orders" | "products" | "settings" | "payouts";
export type NavigationObject = { name: RouteName; path: string };

export const NAVIGATION_OBJECTS: NavigationObject[] = [
  { name: "orders", path: ROUTE_PATHS.ORDERS },
  { name: "batches", path: ROUTE_PATHS.BATCHES },
  { name: "settings", path: ROUTE_PATHS.SETTINGS },
  { name: "products", path: ROUTE_PATHS.PRODUCTS },
  { name: "payouts", path: ROUTE_PATHS.PAYOUTS },
];

// sku import
export const DEFAULT_SHORT_TERM_MIN_WEEKLY_INVENTORY = 500;
export const DEFAULT_SHORT_TERM_MAX_WEEKLY_INVENTORY = 1000;
export const DEFAULT_LONG_TERM_MIN_WEEKLY_INVENTORY = 500;
export const DEFAULT_LONG_TERM_MAX_WEEKLY_INVENTORY = 2000;
export const DEFAULT_PRODUCT_DESCRIPTION_MAX_LENGTH = 65000;
export const DEFAULT_ROASTER_NOTE_MAX_LENGTH = 500;
export const DEFAULT_TITLE_MAX_LENGTH = 40;

// term overrides

export const TERM_OVERRIDE_BAG_SIZE_BY_REGION: { [key in VendorRegion]: string[] | undefined } = {
  BAU: [],
  BUK: [],
  BUS: [],
  BDE: [],
};

export const VALID_AERO_PRESS_FOR_CONNECTION_GRIND: GrindType = "Aero press";

export const AEROPRESS_VARIATIONS = ["Aero press", "Aeropress", "Aero Press", "AeroPress®", "AeroPress"];

export const symbolFromCode = (code: string) => {
  const entry = Object.values(REGIONS_WITH_CURRENCY).find((item) => item.iso.toLowerCase() === code.toLowerCase());
  return entry?.symbol ?? REGIONS_WITH_CURRENCY.BUS.symbol;
};

export const currencyCodes: string[] = Object.values(REGIONS_WITH_CURRENCY).map((item) => item.iso);
